'use client';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { handleServiceError } from '@/app/utils/client';
import { getLiveEmergencyContact, submitLiveEmergencyContact } from '../apis';

type Contact = { name: string; phone: string; relationship: string };
const EMPTY: Contact = { name: '', phone: '', relationship: '' };

export const StreamEmergencyContactInput = () => {
  const t = useTranslations();
  const [contact, setContact] = useState<Contact>(EMPTY);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<Contact>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useFetch(getLiveEmergencyContact, {
    onSuccess: (result) => {
      const { data, error: requestError } = result as Awaited<ReturnType<typeof getLiveEmergencyContact>>;
      if (requestError) {
        handleServiceError(requestError, t);
        return;
      }
      if (data) setContact({ name: data.name ?? '', phone: data.phone ?? '', relationship: data.relationship ?? '' });
    },
    onError: () => toast({ type: 'error', message: t('personal.emergencyContact.loadError') }),
  });

  const { run: submit, loading } = useFetch(() => submitLiveEmergencyContact(contact), {
    manual: true,
    onSuccess: (result) => {
      const { error: requestError } = result as Awaited<ReturnType<typeof submitLiveEmergencyContact>>;
      if (requestError) {
        if (requestError.reason === 'LiveInvalidContactPhone') {
          setError('phoneInvalid');
          toast({ type: 'error', message: t('personal.emergencyContact.errors.phoneInvalid') });
        } else handleServiceError(requestError, t);
        return;
      }
      toast({ type: 'success', message: t('personal.emergencyContact.success') });
      setEditing(false);
      setConfirmOpen(false);
      setExpanded(true);
    },
    onError: () => toast({ type: 'error', message: t('personal.emergencyContact.error') }),
  });

  const handleInputChange = (field: keyof Contact, value: string) => {
    const nextContact = { ...contact, [field]: value };
    setContact(nextContact);
    setError(validateContact(nextContact));
  };

  const validateContact = (value: Contact = contact) => {
    if (!value.name.trim()) return 'nameRequired';
    if (!value.phone.trim()) return 'phoneRequired';
    if (!/^1[3-9]\d{9}$/.test(value.phone.trim())) return 'phoneInvalid';
    if (!value.relationship.trim()) return 'relationshipRequired';
    return null;
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <span className="min-w-0 flex-1 text-[18px]">{t('personal.emergencyContact.label')}</span>
        {expanded && (
          <div className="flex items-center gap-4 font-normal text-[16px] leading-8">
            {editing && (
              <button
                type="button"
                className="text-system-white64"
                onClick={() => {
                  setContact(snapshot);
                  setEditing(false);
                  setExpanded(false);
                  setError(null);
                }}
              >
                {t('common.dialog.cancel')}
              </button>
            )}
            <button
              type="button"
              className={cn(editing ? (error ? 'text-system-white64' : 'text-white') : 'text-system-blue02')}
              disabled={loading}
              onClick={() => {
                if (!editing) {
                  setSnapshot(contact);
                  setEditing(true);
                  setError(null);
                  return;
                }
                const nextError = validateContact();
                setError(nextError);
                if (!nextError) setConfirmOpen(true);
              }}
            >
              {editing ? t('personal.save') : t('personal.edit')}
            </button>
          </div>
        )}
        {!editing && (
          <button
            type="button"
            className="flex size-8 items-center justify-center text-system-white64"
            onClick={() => setExpanded((value) => !value)}
          >
            <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
      </div>
      {expanded && (
        <div className="mb-2">
          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base">{t('personal.emergencyContact.name')}</Label>
            <Input
              value={contact.name}
              disabled={!editing}
              onChange={(event) => handleInputChange('name', event.target.value)}
              placeholder={t('personal.emergencyContact.namePlaceholder')}
              maxLength={30}
            />
          </div>
          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base">{t('personal.emergencyContact.phone')}</Label>
            <Input
              value={contact.phone}
              disabled={!editing}
              onChange={(event) => handleInputChange('phone', event.target.value)}
              placeholder={t('personal.emergencyContact.phonePlaceholder')}
              inputMode="numeric"
              maxLength={11}
            />
          </div>
          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-base">
              {t('personal.emergencyContact.relationship')}
            </Label>
            <Select
              value={contact.relationship}
              onValueChange={(relationship) => handleInputChange('relationship', relationship)}
              disabled={!editing}
            >
              <SelectTrigger disabled={!editing}>
                <SelectValue placeholder={t('personal.emergencyContact.relationshipPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {['parent', 'relative', 'friend', 'other'].map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`personal.emergencyContact.relationship${value[0].toUpperCase()}${value.slice(1)}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2 text-system-white48 text-xs">{t('personal.emergencyContact.description')}</div>
          </div>
          {editing && error && (
            <p className="mb-4 text-sm text-system-error">{t(`personal.emergencyContact.errors.${error}`)}</p>
          )}
        </div>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent closable={false}>
          <div className="font-medium text-xl">{t('personal.emergencyContact.confirmTitle')}</div>
          <div className="mt-2 text-sm">{t('personal.emergencyContact.confirmDescription')}</div>
          <div className="mt-6 flex justify-end gap-4">
            <Button variant="outline" size="dialog" onClick={() => setConfirmOpen(false)}>
              {t('create.createForm.upload.cancel')}
            </Button>
            <Button variant="primary" size="dialog" onClick={() => submit()} loading={loading}>
              {t('create.createForm.upload.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
